/**
 * Configs gear — three modals: Defaults, Dropdown Lists, Data Management.
 * Plus the data-management helpers (export, import, wipe, receipt sweep).
 */
import { sb, CLOUD_USER } from "../lib/supabase";
import { DB, TABLES, TABLE_CONFIG } from "../lib/db";
import { Settings, Lists } from "../lib/prefs";
import { escapeHtml, toast, $ } from "../lib/dom";
import { todayISO } from "../utils/format";
import { openModal, closeModal } from "./modal";
import { render } from "../state";
import { migrateReceiptsToStorage } from "../lib/migrate";
import { refreshAllBulkPoolBasis, BULK_CLASSES, bulkUnitValueFor, getBulkPool } from "../lib/bulk";
import { startBackfill, type BackfillProgress } from "../lib/thumbnailBackfill";
import { startFmvRefreshAll, type FmvRefreshProgress } from "../lib/fmvRefreshAll";
import { generateTaxBundle } from "./printReport";
import { refreshEbayCard } from "../lib/ebayConnect";
import { GRADE_SETS } from "./gradePicker";
import {
  fetchConditionMultipliers, saveConditionMultipliers, isValidMultiplier,
} from "../lib/conditionMultiplier";

/* ============================================================
   Defaults
   ============================================================ */
export function openConfigDefaults(): void {
  const s = Settings.get();
  const body = `
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div><label class="label">IRS mileage rate ($/mi)</label><input class="input" type="number" step="0.001" value="${s.irsRate}" id="setIrs"></div>
      <div><label class="label">Default eBay fee %</label><input class="input" type="number" step="0.01" value="${s.defaultEbayFeePct}" id="setEbay"></div>
      <div><label class="label">Default shipping ($)</label><input class="input" type="number" step="0.01" value="${s.defaultShippingCost}" id="setShip"></div>
      <div><label class="label">Starting cash balance ($)</label><input class="input" type="number" step="0.01" value="${s.startingCashBalance ?? 0}" id="setStartCash" title="Opening cash the business had when you started tracking. Used by the Cash on Hand figure."></div>
      <div class="sm:col-span-2">
        <label class="label">Pokémon TCG API Key (optional, raises rate limit)</label>
        <input class="input" value="${escapeHtml(s.pokemonTcgApiKey || "")}" id="setApi" placeholder="Get one free at pokemontcg.io/dashboard">
      </div>
    </div>

    <div class="mt-4 panel-2 p-3">
      <h4 class="font-semibold text-sm mb-2 text-emerald-300">Bulk pool values</h4>
      <p class="text-xs text-[var(--muted)] mb-3">Per-card nominal values used to compute each bulk pool's basis and FMV. Changes apply to new additions and recompute pool totals on save.</p>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><label class="label">Bulk ($/card)</label><input class="input" type="number" step="0.001" min="0" value="${s.bulkUnitValue}" id="setBulkVal"></div>
        <div><label class="label">Holo Bulk ($/card)</label><input class="input" type="number" step="0.001" min="0" value="${s.holoBulkUnitValue}" id="setHoloBulkVal"></div>
        <div><label class="label">Premium Bulk ($/card)</label><input class="input" type="number" step="0.001" min="0" value="${s.premiumBulkUnitValue}" id="setPremBulkVal"></div>
        <div><label class="label">$1-$5 Bulk ($/card)</label><input class="input" type="number" step="0.01" min="0" value="${s.bulk1to5UnitValue}" id="setBulk1to5Val"></div>
        <div><label class="label">$5-$10 Bulk ($/card)</label><input class="input" type="number" step="0.01" min="0" value="${s.bulk5to10UnitValue}" id="setBulk5to10Val"></div>
      </div>
    </div>

    <div class="text-xs text-[var(--muted)] mt-3">These defaults apply to new entries (Sales fee auto-fill, Mileage deduction, etc.).</div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveSettings();closeModal();render();">Save</button>`;
  openModal({ title: "Configs · Defaults", body, footer, wide: true });
}

function saveSettings(): void {
  Settings.set({
    irsRate:               Number(($("#setIrs")          as HTMLInputElement).value) || 0,
    defaultEbayFeePct:     Number(($("#setEbay")         as HTMLInputElement).value) || 0,
    defaultShippingCost:   Number(($("#setShip")         as HTMLInputElement).value) || 0,
    pokemonTcgApiKey:      ($("#setApi") as HTMLInputElement).value.trim(),
    bulkUnitValue:         Number(($("#setBulkVal")      as HTMLInputElement).value) || 0,
    holoBulkUnitValue:     Number(($("#setHoloBulkVal")  as HTMLInputElement).value) || 0,
    premiumBulkUnitValue:  Number(($("#setPremBulkVal")  as HTMLInputElement).value) || 0,
    bulk1to5UnitValue:     Number(($("#setBulk1to5Val")  as HTMLInputElement).value) || 0,
    bulk5to10UnitValue:    Number(($("#setBulk5to10Val") as HTMLInputElement).value) || 0,
    startingCashBalance:   Number(($("#setStartCash")     as HTMLInputElement).value) || 0,
  });
  // Re-sync any existing bulk pool lots' basis to the new per-card values.
  refreshAllBulkPoolBasis();
  toast("Settings saved");
}

/* ============================================================
   Condition Pricing — the raw-condition multiplier table
   ============================================================ */

/** Kept so Save can write back the same grades the modal rendered. */
let _condGrades: string[] = [];

/**
 * Edit the multipliers applied to catalog market prices for played cards.
 * The numbers live in the `condition_multipliers` table (not in code) so they
 * can be tuned without a deploy — this modal is the front door to that table.
 * Twin logic lives in public.revalue_inventory(); see lib/conditionMultiplier.ts.
 */
export async function openConfigConditions(): Promise<void> {
  _condGrades = GRADE_SETS.Raw.slice();
  const rows = await fetchConditionMultipliers(_condGrades);

  if (!rows) {
    openModal({
      title: "Configs · Condition Pricing",
      body: `<p class="text-sm text-rose-300">Couldn't load the condition multiplier table. Check your connection and try again.</p>`,
      footer: `<button class="btn btn-ghost" onclick="closeModal()">Close</button>`,
    });
    return;
  }

  const body = `
    <p class="text-sm text-[var(--muted)] mb-1">
      What fraction of the Near Mint market price a card in each condition is worth.
      Near Mint is the anchor at 1.00, so these can only discount.
    </p>
    <p class="text-xs text-[var(--muted)] mb-3">
      These are <b>estimates, not market data</b> — every lot they touch records the
      multiplier in its Valuation Source. Changes apply on the next nightly
      revaluation (05:00 UTC) or the next in-app FMV refresh; nothing reprices on save.
    </p>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      ${rows.map((r, i) => `
        <div>
          <label class="label">${escapeHtml(r.grade)}</label>
          <input class="input" type="number" step="0.01" min="0.01" max="1"
                 value="${r.multiplier.toFixed(2)}" id="cond_${i}">
        </div>`).join("")}
    </div>
    <div class="text-xs text-[var(--muted)] mt-3">
      Must be greater than 0 and no more than 1. Vintage cards typically hold more of
      their Near Mint value than modern ones — if your inventory leans one way, tune
      toward it.
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveConditions()">Save</button>`;
  openModal({ title: "Configs · Condition Pricing", body, footer, wide: true });
}

async function saveConditions(): Promise<void> {
  const rows = _condGrades.map((grade, i) => ({
    grade,
    multiplier: Number(($("#cond_" + i) as HTMLInputElement)?.value),
  }));

  const bad = rows.filter(r => !isValidMultiplier(r.multiplier));
  if (bad.length) {
    toast(`Invalid value for ${bad.map(b => b.grade).join(", ")} — must be > 0 and ≤ 1.`, "err");
    return;
  }

  if (await saveConditionMultipliers(rows)) {
    toast("Condition multipliers saved");
    closeModal();
    render();
  } else {
    toast("Couldn't save condition multipliers.", "err");
  }
}

/* ============================================================
   Dropdown Lists
   ============================================================ */
export function openConfigLists(): void {
  const lists = Lists.all();
  const body = `
    <p class="text-sm text-[var(--muted)] mb-3">One value per line. These power the dropdowns across the app (acquisition types, statuses, expense categories, etc.).</p>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
      ${Object.keys(lists).map(name => `
        <div>
          <label class="label">${escapeHtml(name)}</label>
          <textarea class="textarea" rows="6" id="list_${name.replace(/\s+/g, "_")}">${escapeHtml((lists[name] || []).join("\n"))}</textarea>
        </div>
      `).join("")}
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveLists();closeModal();">Save Lists</button>`;
  openModal({ title: "Configs · Dropdown Lists", body, footer, wide: true });
}

function saveLists(): void {
  const obj: Record<string, string[]> = {};
  for (const name of Object.keys(Lists.all())) {
    const ta = $("#list_" + name.replace(/\s+/g, "_")) as HTMLTextAreaElement | null;
    if (!ta) continue;
    obj[name] = (ta.value || "").split("\n").map(s => s.trim()).filter(Boolean);
  }
  Lists.setAll(obj);
  toast("Lists saved");
  render();
}

/* ============================================================
   Data Management — export / import / wipe / receipt sweep
   ============================================================ */
export function openConfigData(): void {
  const body = `
    <div class="space-y-3">
      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-1">Backup & Import</h4>
        <p class="text-xs text-[var(--muted)] mb-2">Export a JSON snapshot of your cloud database, or import a backup file. Import will <b>replace</b> all cloud data after a preview & confirmation step.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-ghost" onclick="exportData()">⤓ Export Cloud Backup (JSON)</button>
          <label class="btn btn-ghost cursor-pointer">⤒ Import Backup<input type="file" class="hidden" accept="application/json" onchange="importData(event)"></label>
          <button class="btn btn-ghost" onclick="openBackupSettings()">⏱ Scheduled Backup to a Folder…</button>
        </div>
      </div>
      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-1">Storage maintenance</h4>
        <p class="text-xs text-[var(--muted)] mb-2">If you imported an older backup, any receipts saved as inline data URLs can be hoisted into the Supabase Storage bucket. This runs automatically at sign-in, but you can re-trigger it here.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-ghost" id="btnSweepReceipts" onclick="_runReceiptSweep()">⤴ Sweep receipts to Storage</button>
        </div>
        <div id="receiptSweepStatus" class="text-xs text-[var(--muted)] mt-2"></div>
      </div>
      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-1">Thumbnails</h4>
        <p class="text-xs text-[var(--muted)] mb-2">Look up an image for every card lot that doesn't have one yet via the Pokémon TCG API (or Scryfall for MTG). Throttled to ~5 requests/second and safe to run anytime; you can cancel mid-run.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-ghost" onclick="openBackfillModal()">⤴ Backfill missing thumbnails</button>
        </div>
        <div class="text-xs text-[var(--muted)] mt-2">Sealed product isn't auto-backfilled — paste URLs manually on the Sealed form.</div>
      </div>
      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-1">FMV refresh</h4>
        <p class="text-xs text-[var(--muted)] mb-2">Walk every card lot with an Image URL and refresh its FMV via the exact-card API (Pokémon TCG / Scryfall). Cards without thumbnails are skipped. The app also runs this automatically on sign-in if the last sweep was more than 24h ago.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-ghost" onclick="openFmvRefreshModal()">↻ Refresh all FMVs</button>
        </div>
        <div class="text-xs text-[var(--muted)] mt-2" id="fmvLastRunHint"></div>
      </div>
      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-1">Books health check</h4>
        <p class="text-xs text-[var(--muted)] mb-2">Run a deterministic set of reconciliation checks across inventory, rips, sales, trades, purchases, cash, and owner balances. Flags anything that doesn't tie out — a good habit before trusting reports or filing taxes.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-ghost" onclick="openBooksHealth()">🩺 Run books health check</button>
        </div>
      </div>
      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-1">Rip cost basis</h4>
        <p class="text-xs text-[var(--muted)] mb-2">Re-check that every box's cost is split across the cards it produced without double-counting, and correct the cost basis / realized gain booked on past sales &amp; trades of ripped cards. You review a full before/after table before anything is written.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-ghost" onclick="openRipBasisRepair()">⚖ Review &amp; repair rip cost basis</button>
        </div>
      </div>
      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-1">eBay connection</h4>
        <p class="text-xs text-[var(--muted)] mb-2">Connect your eBay seller account to list items straight from inventory (listing builder coming next). You sign in on eBay itself \u2014 Enigma Ledger never sees your password, and access can be revoked anytime from either side.</p>
        <div id="ebayConnStatus" class="text-xs text-[var(--muted)] mb-2">Checking\u2026</div>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-ghost" id="btnEbayConnect" onclick="_ebayConnect()">\u26a1 Connect eBay</button>
          <button class="btn btn-ghost hidden" id="btnEbayDisconnect" onclick="_ebayDisconnect()">\u2715 Disconnect</button>
        </div>
      </div>
      <div class="panel-2 p-3" style="border-color: rgba(239,68,68,.4)">
        <h4 class="font-semibold mb-1 text-rose-300">Danger Zone</h4>
        <p class="text-xs text-[var(--muted)] mb-2">This permanently deletes every record in your cloud account. Export a backup first.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-danger" onclick="wipeAll();closeModal();">Wipe All Cloud Data</button>
        </div>
      </div>
      <div class="panel-2 p-3 text-sm text-[var(--muted)]">
        <div><b>Storage:</b> Your data lives in your private Supabase cloud database (tied to your sign-in). It's the same on every device you sign in from.</div>
        <div class="mt-2"><b>Tip:</b> Export a backup every so often as a safety net. Imports always preview the file and ask for confirmation.</div>
      </div>
    </div>`;
  const footer = `<button class="btn btn-ghost" onclick="closeModal()">Close</button>`;
  openModal({ title: "Configs · Data Management", body, footer, wide: true });
  void refreshEbayCard();
}

/* ============================================================
   Export
   ============================================================ */
function exportData(): void {
  const stripUuid = (rows: any[]) => (rows || []).map(r => {
    const { _uuid, ...rest } = r;
    return rest;
  });
  const payload: Record<string, any> = { _version: 2, exportedAt: new Date().toISOString() };
  for (const t of TABLES) payload[t] = stripUuid(DB.table(t));
  payload._lists = Lists.all();
  payload._settings = Settings.get();
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `enigma-cloud-backup-${todayISO()}.json`;
  a.click();
  toast("Backup downloaded");
}

/* ============================================================
   Import (with preview + progress UI)
   ============================================================ */
let _migrationPayload: any = null;

function importData(ev: Event): void {
  const file = (ev.target as HTMLInputElement).files?.[0];
  (ev.target as HTMLInputElement).value = "";
  if (!file) return;
  const r = new FileReader();
  r.onload = e => {
    let obj: any;
    try { obj = JSON.parse((e.target as FileReader).result as string); }
    catch (err: any) { toast("Invalid JSON file: " + err.message, "err"); return; }
    if (!obj || typeof obj !== "object") { toast("Backup file is not a valid Enigma export.", "err"); return; }
    closeModal(); // close the Configs modal that triggered the file picker
    openMigrationModal(obj);
  };
  r.readAsText(file);
}

export function openMigrationModal(payload: Record<string, any>): void {
  const counts: Record<string, number> = {};
  let total = 0;
  for (const t of TABLES) {
    const n = Array.isArray(payload[t]) ? payload[t].length : 0;
    counts[t] = n;
    total += n;
  }
  const childCounts: Record<string, number> = {
    "Purchase items": 0, "Sale items": 0, "Trade outgoing": 0, "Trade incoming": 0, "Grading cards": 0,
  };
  (payload.purchases || []).forEach((p: any) => { childCounts["Purchase items"] += (p.Items || []).length; });
  (payload.sales || [])    .forEach((s: any) => { childCounts["Sale items"]     += (s.Items || []).length; });
  (payload.trades || [])   .forEach((t: any) => {
    childCounts["Trade outgoing"] += (t.Outgoing || []).length;
    childCounts["Trade incoming"] += (t.Incoming || []).length;
  });
  (payload.grading || []).forEach((g: any) => { childCounts["Grading cards"] += (g.Cards || []).length; });

  const exportedAt = payload.exportedAt ? new Date(payload.exportedAt).toLocaleString() : "(unknown)";
  const version = payload._version ?? "(unknown)";
  const hasLists = !!payload._lists;
  const hasSettings = !!payload._settings;

  const parentRows = TABLES.map(t => `
    <tr>
      <td class="py-1 pr-3">${escapeHtml(t)}</td>
      <td class="py-1 text-right tabular-nums">${counts[t]}</td>
    </tr>`).join("");
  const childRows = Object.keys(childCounts).map(k => `
    <tr>
      <td class="py-1 pr-3 text-[var(--muted)]">↳ ${escapeHtml(k)}</td>
      <td class="py-1 text-right tabular-nums text-[var(--muted)]">${childCounts[k]}</td>
    </tr>`).join("");

  const body = `
    <div class="space-y-3 text-sm">
      <div class="panel-2 p-3 text-xs space-y-1">
        <div><b class="text-emerald-300">Merge (add missing)</b> — adds records from this backup you don't already have. Never deletes or overwrites your current data. Safe to run anytime.</div>
        <div><b class="text-amber-300">Replace everything</b> — wipes your entire account and rebuilds it exactly from this file. There is no undo.</div>
      </div>
      <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div><b>Exported:</b> ${escapeHtml(exportedAt)}</div>
        <div><b>Format version:</b> ${escapeHtml(String(version))}</div>
        <div><b>Lists in file:</b> ${hasLists ? "yes" : "no"}</div>
        <div><b>Settings in file:</b> ${hasSettings ? "yes" : "no"}</div>
      </div>
      <div class="panel-2 p-3">
        <div class="text-xs text-[var(--muted)] mb-2">Records to import</div>
        <table class="w-full text-xs"><tbody>${parentRows}${childRows}</tbody></table>
        <div class="mt-2 pt-2 border-t border-[var(--border)] text-xs flex justify-between">
          <span>Top-level rows</span><b class="tabular-nums">${total}</b>
        </div>
      </div>
      <div id="migrateProgress" class="hidden space-y-2 pt-3 border-t border-[var(--border)]">
        <div class="text-sm font-medium" id="migrateLabel">Preparing…</div>
        <div class="h-2 rounded bg-white/10 overflow-hidden">
          <div id="migrateBar" class="h-full bg-emerald-500 transition-all" style="width:0%"></div>
        </div>
        <div class="text-xs text-[var(--muted)]" id="migrateDetail"></div>
      </div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" id="btnMigrateCancel" onclick="_cancelMigration()">Cancel</button>
    <button class="btn btn-ghost" id="btnMigrateGo" onclick="_runMigration()">Replace everything</button>
    <button class="btn btn-primary" id="btnMergeGo" onclick="_runMerge()">Merge (add missing)</button>`;
  openModal({ title: "Import from local backup", body, footer, wide: false });
  _migrationPayload = payload;
}

function _cancelMigration(): void { _migrationPayload = null; closeModal(); }

async function _runMigration(): Promise<void> {
  if (!_migrationPayload) return;
  const payload = _migrationPayload;
  const btnGo = document.getElementById("btnMigrateGo") as HTMLButtonElement | null;
  const btnCancel = document.getElementById("btnMigrateCancel") as HTMLButtonElement | null;
  const prog = document.getElementById("migrateProgress");
  const bar = document.getElementById("migrateBar") as HTMLElement | null;
  const label = document.getElementById("migrateLabel");
  const detail = document.getElementById("migrateDetail");
  if (btnGo) { btnGo.disabled = true; btnGo.textContent = "Replacing…"; }
  (document.getElementById("btnMergeGo") as HTMLButtonElement | null)?.setAttribute("disabled", "true");
  if (btnCancel) btnCancel.disabled = true;
  prog?.classList.remove("hidden");
  try {
    await DB.replaceAll(payload, (step) => {
      if (bar) bar.style.width = step.pct + "%";
      if (label) label.textContent = step.label;
      if (detail) detail.textContent = step.detail || "";
    });
    if (payload._lists)    Lists.setAll(payload._lists);
    if (payload._settings) Settings.set(payload._settings);
    if (bar) bar.style.width = "100%";
    if (label) label.textContent = "Done!";
    if (detail) detail.textContent = "Your cloud database now matches your backup file.";
    toast("Import complete", "ok");
    _migrationPayload = null;
    setTimeout(() => { closeModal(); render(); }, 700);
  } catch (e: any) {
    console.error("[migrate] failed:", e);
    if (label) label.textContent = "Import failed";
    if (detail) detail.textContent = (e?.message || String(e)) + " — your cloud data may be in a partial state. Retry, or use Wipe All Data to start clean.";
    if (btnGo)     { btnGo.disabled = false; btnGo.textContent = "Retry"; }
    if (btnCancel) btnCancel.disabled = false;
    toast("Import failed: " + (e?.message || e), "err");
  }
}

/* ============================================================
   Merge (non-destructive) — add records from a backup that aren't
   already present. Keyed tables dedupe by business ID; keyless tables
   (expenses / mileage / buys / time entries) dedupe by exact content.
   ============================================================ */
function _stableSig(row: any): string {
  const { _uuid, ...rest } = row || {};
  const norm: Record<string, any> = {};
  for (const k of Object.keys(rest).sort()) norm[k] = rest[k];
  return JSON.stringify(norm);
}

async function mergeMissing(
  payload: any,
  onStep: (s: { pct: number; label: string; detail?: string }) => void,
): Promise<{ added: number; skipped: number }> {
  let added = 0, skipped = 0;
  for (let ti = 0; ti < TABLES.length; ti++) {
    const t = TABLES[ti];
    const rows: any[] = Array.isArray(payload[t]) ? payload[t] : [];
    const tcfg = TABLE_CONFIG[t];
    const existing = DB.table(t);
    onStep({ pct: Math.round((ti / TABLES.length) * 100), label: `Merging ${t}…`, detail: `${rows.length} in file` });
    if (tcfg.pkKey) {
      const key = tcfg.pkKey;
      const have = new Set(existing.map(r => String(r[key] ?? "")));
      for (const row of rows) {
        const k = String(row[key] ?? "");
        if (!k || have.has(k)) { skipped++; continue; }
        await DB.add(t, { ...row });
        have.add(k);
        added++;
      }
    } else {
      const have = new Set(existing.map(_stableSig));
      for (const row of rows) {
        const sig = _stableSig(row);
        if (have.has(sig)) { skipped++; continue; }
        await DB.add(t, { ...row });
        have.add(sig);
        added++;
      }
    }
  }
  // Dropdown lists: union in any new options; never remove the user's existing ones.
  if (payload._lists && typeof payload._lists === "object") {
    const cur = Lists.all();
    const merged: Record<string, string[]> = { ...cur };
    for (const k of Object.keys(payload._lists)) {
      const a = Array.isArray(cur[k]) ? cur[k] : [];
      const b = Array.isArray(payload._lists[k]) ? payload._lists[k] : [];
      merged[k] = Array.from(new Set([...a, ...b]));
    }
    Lists.setAll(merged);
  }
  // Settings are intentionally left untouched on a merge.
  onStep({ pct: 100, label: "Done!", detail: `${added} added · ${skipped} already present` });
  return { added, skipped };
}

async function _runMerge(): Promise<void> {
  if (!_migrationPayload) return;
  const payload = _migrationPayload;
  const btnReplace = document.getElementById("btnMigrateGo") as HTMLButtonElement | null;
  const btnMerge = document.getElementById("btnMergeGo") as HTMLButtonElement | null;
  const btnCancel = document.getElementById("btnMigrateCancel") as HTMLButtonElement | null;
  const prog = document.getElementById("migrateProgress");
  const bar = document.getElementById("migrateBar") as HTMLElement | null;
  const label = document.getElementById("migrateLabel");
  const detail = document.getElementById("migrateDetail");
  if (btnMerge) { btnMerge.disabled = true; btnMerge.textContent = "Merging…"; }
  if (btnReplace) btnReplace.disabled = true;
  if (btnCancel) btnCancel.disabled = true;
  prog?.classList.remove("hidden");
  try {
    const res = await mergeMissing(payload, (step) => {
      if (bar) bar.style.width = step.pct + "%";
      if (label) label.textContent = step.label;
      if (detail) detail.textContent = step.detail || "";
    });
    if (label) label.textContent = "Merge complete";
    if (detail) detail.textContent = `${res.added} record(s) added · ${res.skipped} already present.`;
    toast(`Merge complete — ${res.added} added`, "ok");
    _migrationPayload = null;
    setTimeout(() => { closeModal(); render(); }, 900);
  } catch (e: any) {
    console.error("[merge] failed:", e);
    if (label) label.textContent = "Merge failed";
    if (detail) detail.textContent = (e?.message || String(e)) + " — some records may already be added; retrying skips anything present.";
    if (btnMerge) { btnMerge.disabled = false; btnMerge.textContent = "Retry merge"; }
    if (btnReplace) btnReplace.disabled = false;
    if (btnCancel) btnCancel.disabled = false;
    toast("Merge failed: " + (e?.message || e), "err");
  }
}

/* ============================================================
   Wipe + receipt sweep
   ============================================================ */
async function wipeAll(): Promise<void> {
  // Capture locally so TS flow narrowing works across awaits.
  const user = CLOUD_USER;
  if (!user) { toast("Not signed in", "err"); return; }
  if (!confirm("Wipe ALL data in your CLOUD account? This deletes every record across every table and cannot be undone.")) return;
  try {
    for (const t of Object.keys(TABLE_CONFIG)) {
      const cfg = TABLE_CONFIG[t as keyof typeof TABLE_CONFIG];
      const { error } = await sb.from(cfg.pgTable).delete().eq("user_id", user.id);
      if (error) throw error;
    }
    DB._all = { _version: 2 };
    for (const t of TABLES) { DB._all[t] = []; DB._uuid[t] = {}; }
    DB.save();
    toast("All cloud data wiped");
    render();
  } catch (e: any) {
    console.error("[wipeAll]", e);
    toast("Wipe failed: " + (e?.message || e), "err");
  }
}

async function _runReceiptSweep(): Promise<void> {
  const btn = document.getElementById("btnSweepReceipts") as HTMLButtonElement | null;
  const status = document.getElementById("receiptSweepStatus");
  if (btn) btn.disabled = true;
  if (status) status.textContent = "Scanning expenses…";
  try {
    const result = await migrateReceiptsToStorage((p) => {
      if (status) status.textContent = `Uploading ${p.current} / ${p.total}…`;
    });
    if (status) {
      if (result.migrated) {
        status.textContent = `Migrated ${result.migrated} receipt${result.migrated === 1 ? "" : "s"}.${result.failed ? ` ${result.failed} failed (see console).` : ""}`;
      } else {
        status.textContent = `Nothing to migrate — ${result.skipped} expense row${result.skipped === 1 ? "" : "s"} scanned.`;
      }
    }
  } catch (e: any) {
    if (status) status.textContent = "Sweep failed: " + (e?.message || e);
  }
  if (btn) btn.disabled = false;
}

/* ============================================================
   Backfill thumbnails — progress modal + start/stop wiring
   ============================================================ */
let _backfillToken: { cancel: () => void } | null = null;

export function openBackfillModal(): void {
  const body = `
    <div class="space-y-3">
      <p class="text-sm text-[var(--muted)]">Scans every card lot without an image and tries to find one via the Pokémon TCG API (or Scryfall for MTG). Existing images aren't overwritten.</p>
      <div id="backfillStatus" class="panel-2 p-3 text-sm">Ready. Click <b>Start</b> below to begin.</div>
      <div class="text-xs text-[var(--muted)]">Throttle: ~5 requests/second. You can close this modal at any time — it stops the run.</div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="stopBackfill();closeModal()">Cancel</button>
    <button class="btn btn-primary" id="backfillStartBtn" onclick="runBackfill()">Start</button>`;
  openModal({ title: "Backfill missing thumbnails", body, footer });
}

function runBackfill(): void {
  if (_backfillToken) return; // already running
  const startBtn = document.getElementById("backfillStartBtn") as HTMLButtonElement | null;
  const statusEl = document.getElementById("backfillStatus");
  if (startBtn) { startBtn.disabled = true; startBtn.textContent = "Running…"; }

  const renderStatus = (p: BackfillProgress) => {
    if (!statusEl) return;
    const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
    statusEl.innerHTML = `
      <div class="mb-2 flex justify-between text-xs"><span><b>${p.done}</b> / ${p.total}</span><span>${pct}%</span></div>
      <div class="h-2 rounded bg-[var(--border)] overflow-hidden"><div class="h-full bg-emerald-400" style="width:${pct}%"></div></div>
      <div class="mt-2 text-xs grid grid-cols-3 gap-2">
        <div>Filled: <b class="text-emerald-300">${p.filled}</b></div>
        <div>Skipped: <b class="text-[var(--muted)]">${p.skipped}</b></div>
        <div>Failed: <b class="text-rose-300">${p.failed}</b></div>
      </div>
      ${p.currentName ? `<div class="mt-1 text-xs text-[var(--muted)] truncate">Looking up: ${escapeHtml(p.currentName)}</div>` : ""}`;
  };

  const tok = startBackfill(renderStatus);
  _backfillToken = tok;
  tok.promise.finally(() => {
    _backfillToken = null;
    if (startBtn) { startBtn.disabled = false; startBtn.textContent = "Done"; }
    render();
  });
}

function stopBackfill(): void {
  if (_backfillToken) { _backfillToken.cancel(); _backfillToken = null; }
}

(window as any).openBackfillModal = openBackfillModal;
(window as any).runBackfill       = runBackfill;
(window as any).stopBackfill      = stopBackfill;

/* ============================================================
   FMV sweep — manual progress modal + start/stop wiring
   ============================================================ */
let _fmvToken: { cancel: () => void } | null = null;

export function openFmvRefreshModal(): void {
  const lastIso = Settings.get().lastFmvRefreshAt;
  const lastLabel = lastIso
    ? `Last automated sweep: <b>${escapeHtml(new Date(lastIso).toLocaleString())}</b>`
    : `<i>No sweep has run yet on this account.</i>`;
  const body = `
    <div class="space-y-3">
      <p class="text-sm text-[var(--muted)]">Walks every card with an Image URL and updates its FMV via the exact-card API. Skips bulk pool singletons, cards without thumbnails, and <b>graded slabs</b> (image matching returns the raw card price, which under-values a slab - graded items should be priced manually). Throttled to ~4-5 requests per second.</p>
      <p class="text-xs text-[var(--muted)]">Runs automatically on the first load of each calendar day. You can also trigger it here at any time.</p>
      <div class="text-xs panel-2 p-2" id="fmvLastRow">${lastLabel}</div>
      <div id="fmvSweepStatus" class="panel-2 p-3 text-sm">Ready. Click <b>Start</b> to begin.</div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="stopFmvRefresh();closeModal()">Cancel</button>
    <button class="btn btn-primary" id="fmvSweepStartBtn" onclick="runFmvRefresh()">Start</button>`;
  openModal({ title: "Refresh all FMVs", body, footer });
}

function runFmvRefresh(): void {
  if (_fmvToken) return;
  const startBtn = document.getElementById("fmvSweepStartBtn") as HTMLButtonElement | null;
  const statusEl = document.getElementById("fmvSweepStatus");
  if (startBtn) { startBtn.disabled = true; startBtn.textContent = "Running…"; }

  const renderStatus = (p: FmvRefreshProgress) => {
    if (!statusEl) return;
    const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
    statusEl.innerHTML = `
      <div class="mb-2 flex justify-between text-xs"><span><b>${p.done}</b> / ${p.total}</span><span>${pct}%</span></div>
      <div class="h-2 rounded bg-[var(--border)] overflow-hidden"><div class="h-full bg-emerald-400" style="width:${pct}%"></div></div>
      <div class="mt-2 text-xs grid grid-cols-3 gap-2">
        <div>Updated: <b class="text-emerald-300">${p.updated}</b></div>
        <div>Skipped: <b class="text-[var(--muted)]">${p.skipped}</b></div>
        <div>Failed: <b class="text-rose-300">${p.failed}</b></div>
      </div>
      ${p.currentName ? `<div class="mt-1 text-xs text-[var(--muted)] truncate">Looking up: ${escapeHtml(p.currentName)}</div>` : ""}`;
  };

  const tok = startFmvRefreshAll(renderStatus);
  _fmvToken = tok;
  tok.promise.finally(() => {
    _fmvToken = null;
    if (startBtn) { startBtn.disabled = false; startBtn.textContent = "Done"; }
    render();
  });
}

function stopFmvRefresh(): void {
  if (_fmvToken) { _fmvToken.cancel(); _fmvToken = null; }
}

(window as any).openFmvRefreshModal = openFmvRefreshModal;
(window as any).runFmvRefresh       = runFmvRefresh;
(window as any).stopFmvRefresh      = stopFmvRefresh;

/* ============================================================
   Tax bundle — year picker → renders the full year-end PDF
   ============================================================ */
export function openConfigTaxBundle(): void {
  const thisYear = new Date().getFullYear();
  const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3];
  const body = `
    <div class="space-y-3">
      <p class="text-sm">Generates a year-end PDF bundle including:</p>
      <ul class="text-xs text-[var(--muted)] list-disc pl-5 space-y-1">
        <li>Cover summary &mdash; gross revenue, COGS, fees+ship, deductible expenses, mileage deduction, approx. net income</li>
        <li>Income statement (P&amp;L) for the year</li>
        <li>Suggested Schedule C line mapping</li>
        <li>Balance-sheet snapshot &mdash; cash on hand, inventory at cost, due-to-owner, owner equity</li>
        <li>Sales register for the year with totals</li>
        <li>Purchases register for the year with totals</li>
        <li>Expenses grouped by category with subtotals + deductible amounts</li>
        <li>Mileage log + IRS-rate deduction</li>
        <li>Current inventory snapshot (Cards / Sealed / Bulk pools)</li>
      </ul>
      <div>
        <label class="label" for="taxYear">Tax year</label>
        <select class="select" id="taxYear">
          ${years.map((y, i) => `<option value="${y}" ${i === 0 ? "selected" : ""}>${y}</option>`).join("")}
        </select>
      </div>
      <div class="text-xs text-[var(--muted)] panel-2 p-2">
        Note: the inventory snapshot reflects <b>current</b> state, not Dec 31 of the chosen year.
        For accurate year-end inventory, generate this bundle close to January 1.
      </div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="runTaxBundle()">Generate PDF</button>`;
  openModal({ title: "Tax Bundle", body, footer });
}

function runTaxBundle(): void {
  const yearInp = document.getElementById("taxYear") as HTMLSelectElement | null;
  const year = Number(yearInp?.value) || new Date().getFullYear();
  const settings = Settings.get();
  const pools = BULK_CLASSES.map(klass => {
    const pool = getBulkPool(klass);
    return {
      klass,
      qty: pool ? Number(pool["Qty"]) || 0 : 0,
      unitValue: bulkUnitValueFor(klass),
    };
  });
  generateTaxBundle(year, {
    sales:     DB.table("sales"),
    purchases: DB.table("purchases"),
    expenses:  DB.table("expenses"),
    mileage:   DB.table("mileage"),
    cards:     DB.table("cards"),
    sealed:    DB.table("sealed"),
    trades:    DB.table("trades"),
    transactions: DB.table("transactions"),
  }, settings.irsRate, pools);
  closeModal();
}

(window as any).runTaxBundle = runTaxBundle;

/* ============================================================
   Configs gear button → small popover menu
   ============================================================ */
export function openConfigsMenu(anchor: HTMLElement): void {
  // Remove any existing popover before opening a new one
  document.getElementById("configsMenuPopover")?.remove();
  const rect = anchor.getBoundingClientRect();
  const pop = document.createElement("div");
  pop.id = "configsMenuPopover";
  pop.className = "fixed panel p-1 z-[70] shadow-2xl min-w-[200px]";
  pop.style.top   = (rect.bottom + 4) + "px";
  pop.style.right = (window.innerWidth - rect.right) + "px";
  pop.innerHTML = `
    <button class="nav-item w-full text-left" data-cfg="defaults"><span class="text-lg">⚙</span><span>Defaults</span></button>
    <button class="nav-item w-full text-left" data-cfg="lists"><span class="text-lg">≡</span><span>Dropdown Lists</span></button>
    <button class="nav-item w-full text-left" data-cfg="conditions"><span class="text-lg">◈</span><span>Condition Pricing</span></button>
    <button class="nav-item w-full text-left" data-cfg="tax"><span class="text-lg">📑</span><span>Tax Bundle</span></button>
    <button class="nav-item w-full text-left" data-cfg="data"><span class="text-lg">⤓</span><span>Data Management</span></button>`;
  document.body.appendChild(pop);
  pop.addEventListener("click", (e) => {
    const key = (e.target as HTMLElement).closest("[data-cfg]")?.getAttribute("data-cfg");
    if (!key) return;
    closeConfigsMenu();
    if (key === "defaults")   openConfigDefaults();
    if (key === "lists")      openConfigLists();
    if (key === "conditions") void openConfigConditions();
    if (key === "tax")        openConfigTaxBundle();
    if (key === "data")       openConfigData();
  });
  // Click-outside closes
  const dismiss = (e: MouseEvent) => {
    if (!pop.contains(e.target as Node) && e.target !== anchor) {
      closeConfigsMenu();
      document.removeEventListener("mousedown", dismiss);
    }
  };
  setTimeout(() => document.addEventListener("mousedown", dismiss), 0);
}

function closeConfigsMenu(): void {
  document.getElementById("configsMenuPopover")?.remove();
}

// Inline-handler bridges (called from button onclick=... in the modal HTML)
(window as any).saveSettings        = saveSettings;
(window as any).saveLists           = saveLists;
(window as any).saveConditions      = saveConditions;
(window as any).exportData          = exportData;
(window as any).importData          = importData;
(window as any).wipeAll             = wipeAll;
(window as any)._runReceiptSweep    = _runReceiptSweep;
(window as any)._cancelMigration    = _cancelMigration;
(window as any)._runMigration       = _runMigration;
(window as any)._runMerge            = _runMerge;
